<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useQuasar } from 'quasar';
import FlightSchoolAdminsTable, { type AdminRow } from '@/components/AdminCenter/FlightSchoolAdminsTable.vue';
import AdminRepository from '@/repositories/AdminRepository';
import type { TypedColumns } from '@/types/QuasarOverrides';
import { useSchoolStore } from '@/stores/school';
import type { EmailSelectionOption } from '@/types/FlightSchoolAdmin';
import AddAdministrator from '@/components/AdminCenter/AddAdministrator.vue';
import RemoveAdministratorDialog from '@/components/AdminCenter/RemoveAdministratorDialog.vue';
import { useUserStore } from '@/stores/user';

const $q = useQuasar();

defineProps<{
    schoolId: number;
}>();

const schoolStore = useSchoolStore();

const loading = ref<boolean>(true);
const adminRows = ref<AdminRow[]>([]);
const selectedAdmins = ref<AdminRow[]>([]);
const addingAdmins = ref<boolean>(false);

const loadAdmins = async () => {
    if (!schoolStore.school) {
        return;
    }

    const admins = await AdminRepository.getSchoolAdmins(schoolStore.school);
    adminRows.value = admins
        .sort((a, b) => Number(b.isOwner) - Number(a.isOwner))
        .map((admin) => ({
            ...admin,
            emailSubscriber: {
                label: admin.emailSubscriber ? 'Yes' : 'No',
                value: admin.emailSubscriber,
            },
        }));
};

const updateAdminRowEmailSubscription = (admin: AdminRow, value: boolean) => {
    adminRows.value = adminRows.value.map((a) => {
        if (a.id === admin.id) {
            return {
                ...a,
                emailSubscriber: {
                    label: value ? 'Yes' : 'No',
                    value,
                },
            };
        }
        return a;
    });
};

const schoolHasEmailSubscriber = computed(() => {
    if (adminRows.value.length === 0) {
        return false;
    }

    return adminRows.value.filter((a) => a.emailSubscriber.value).length > 0;
});

const notifyNoEmailSubscriber = () => {
    $q.notify({
        message: 'At least one admin must be subscribed to emails.',
        color: 'negative',
    });
};

const updateEmailSubscription = async (admin: AdminRow, value: boolean) => {
    if (!schoolStore.school) {
        return;
    }

    // check for the last email subscriber. if it is the last one, don't allow it to be unsubscribed
    if (!value && !schoolHasEmailSubscriber.value) {
        notifyNoEmailSubscriber();

        // revert the email subscription value
        updateAdminRowEmailSubscription(admin, !value);
        return;
    }

    try {
        await AdminRepository.updateAdminEmailSubscriptions(schoolStore.school, admin.id, value);
        updateAdminRowEmailSubscription(admin, value);
    } catch (error: unknown) {
        // todo: log error
        if (error instanceof Error) {
            $q.notify({
                message: `Error updating email subscription: ${error.message}`,
                color: 'negative',
            });
        }

        updateAdminRowEmailSubscription(admin, !value);
    }
};

const deleteAdmins = async (admins: AdminRow[]) => {
    if (admins.length === 0) {
        return;
    }

    const { school } = schoolStore;

    if (!school) {
        return;
    }

    const remainingAdmins = adminRows.value.filter((admin) => !admins.includes(admin));
    const remainingEmailSubscribers = remainingAdmins.filter(
        (admin) => admin.emailSubscriber.value,
    );

    // if there are no remaining email subscribers, don't allow the admins to be deleted
    if (remainingEmailSubscribers.length === 0) {
        notifyNoEmailSubscriber();
        return;
    }

    try {
        const deletedAdmins: number[] = [];

        await Promise.all(admins.map(async (admin) => {
            await AdminRepository.deleteCourseAdmin(school, admin.id);
            return deletedAdmins.push(admin.id);
        }));

        // remove deleted admins from the table
        adminRows.value = adminRows.value.filter((admin) => !deletedAdmins.includes(admin.id));
        selectedAdmins.value = selectedAdmins.value.filter((
            (admin) => !deletedAdmins.includes(admin.id)
        ));

        // show a notification on successful deletion
        const message = `${deletedAdmins.length} admin${deletedAdmins.length > 1 ? 's' : ''} deleted.`;
        const color = 'positive';
        $q.notify({ message, color });
    } catch (error: unknown) {
        // todo: log error
        if (error instanceof Error) {
            $q.notify({
                message: `Error deleting admin: ${error.message}`,
                color: 'negative',
            });
        }
    }
};

/**
 * Prompt the user with a Custom Quasar Dialog to confirm the deletion of the selected admins.
 * Upon confirmation, delete the admins.
 */
const promptDeleteAdmins = (admins: AdminRow[]) => {
    $q.dialog({
        component: RemoveAdministratorDialog,
        componentProps: {
            title: 'Confirm Your Selection',
            message: 'You have selected these administrators to be removed from this school:',
            admins: admins.map((admin) => admin.name),
            persistent: true,
        },
    }).onOk(() => {
        deleteAdmins(admins);
    });
};

const refreshAdmins = async () => {
    loading.value = true;
    addingAdmins.value = false;
    await loadAdmins();
    loading.value = false;
};

const emailSubscriberSort = (
    { value: a }: EmailSelectionOption,
    { value: b }: EmailSelectionOption,
) => {
    if (a === b) {
        return 0;
    }

    return a ? -1 : 1;
};

// Global Admins Table Columns
const columns: TypedColumns = [
    {
        name: 'name',
        label: 'Name',
        field: 'name',
        align: 'left',
        sortable: true,
    },
    {
        name: 'id',
        label: 'ID',
        field: 'id',
        align: 'left',
        sortable: true,
    },
    {
        name: 'role',
        label: 'Role',
        field: 'isOwner',
        align: 'left',
        sortable: true,
    },
    {
        name: 'email',
        label: 'Email',
        field: 'email',
        align: 'left',
        sortable: true,
    },
    {
        name: 'address',
        label: 'Address',
        field: 'address',
        align: 'left',
    },
    {
        name: 'phoneNumbers',
        label: 'Phone Number(s)',
        field: 'phoneNumbers',
        align: 'left',
    },
    {
        name: 'emailSubscriber',
        label: 'Subscribed to Emails',
        field: 'emailSubscriber',
        align: 'left',
        sortable: true,
        sort: emailSubscriberSort,
    },
];

// School Owner (primary administrator) Table Columns
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const schoolOwnerColumns: TypedColumns = [
    {
        name: 'name',
        label: 'Name',
        field: 'name',
        align: 'left',
    },
    {
        name: 'role',
        label: 'Role',
        field: 'isOwner',
        align: 'left',
        sortable: true,
    },
    {
        name: 'email',
        label: 'Email',
        field: 'email',
        align: 'left',
    },
    {
        name: 'emailSubscriber',
        label: 'Subscribed to Emails',
        field: 'emailSubscriber',
        align: 'left',
    },
];

const columnsToDisplay = computed(() => {
    const userStore = useUserStore();

    // if the user is a global admin, show all columns
    if (userStore.isGlobalAdmin) {
        return columns;
    }

    return schoolOwnerColumns;
});

onMounted(async () => {
    await loadAdmins();
    loading.value = false;
});
</script>

<template>
    <!-- eslint-disable max-len -->
    <div class="q-pa-md">
        <div class="container">
            <div class="row justify-center">
                <div
                    v-if="schoolStore.school"
                    class="col col-lg-10"
                >
                    <div class="row justify-end">
                        <q-btn
                            v-if="!addingAdmins"
                            id="add-administrators"
                            class="fsum-button-dark-blue q-mb-md"
                            label="Add Administrator"
                            no-caps
                            @click="addingAdmins = !addingAdmins"
                        />
                    </div>
                    <AddAdministrator
                        v-if="addingAdmins"
                        :school="schoolStore.school"
                        @update:go-back="refreshAdmins"
                    />
                    <FlightSchoolAdminsTable
                        v-else
                        :rows="adminRows"
                        :columns="columnsToDisplay"
                        :loading="loading"
                        selection="multiple"
                        :selected="selectedAdmins"
                        @update:selected="selectedAdmins = $event"
                        @update:subscribed="(admin: AdminRow, value: boolean) => updateEmailSubscription(admin, value)"
                        @delete:admins="promptDeleteAdmins"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
</style>
